import { createRouter, createWebHistory } from 'vue-router';
import { useUserStore } from '@/core/store/useUserStore';
import { useSubscriptionStore } from '@/core/store/useSubscriptionStore';
import { useWorkerStore } from '@/core/store/useWorkerStore';
import { showPermissionBlocked } from '@/utils/permission';
import { featurePermissionAlpha, runtimeEnv } from '@/config/featureFlags';
import { i18n, supportedLanguages, defaultLanguage, getLocaleFromPath, getUserPreferredLanguage } from '@/i18n';
import { evaluateOnRouteEntry } from '@/core/billing/billingNoticeManager';
import { recordNavigationTrigger } from '@/core/analytics/navigationTrigger';
import AppLayout from '@/layouts/AppLayout.vue';
import LandingLayout from '@/layouts/LandingLayout.vue';
import LoginView from '@/views/LoginView.vue';

const getPerfNow = () => (
    typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : Date.now()
);

const createAsyncRouteComponentLoader = (label, importer) => {
    return async () => {
        const startedAt = getPerfNow();
        console.log(`[PSDBG][route-loader] ${label} load start`, {
            startedAt
        });
        try {
            const mod = await importer();
            console.log(`[PSDBG][route-loader] ${label} load success`, {
                durationMs: Math.round((getPerfNow() - startedAt) * 100) / 100
            });
            return mod;
        } catch (error) {
            console.error(`[PSDBG][route-loader] ${label} load failed`, {
                durationMs: Math.round((getPerfNow() - startedAt) * 100) / 100,
                message: error?.message || error
            });
            throw error;
        }
    };
};

const routes = [
    {
        path: '/sitemap.xml',
        beforeEnter: (to, from, next) => {
            next();
        }
    },
    {
        path: '/robots.txt',
        beforeEnter: (to, from, next) => {
            next();
        }
    },
    {
        // Stripe 回跳兼容入口：后端回跳固定为无 locale 的 /dashboard。
        // 归一化为 /{preferredLocale}/dashboard，并原样保留 query 与 hash。
        // 放在 /:lang 之前，避免被当作 lang=dashboard 误消费。
        path: '/dashboard',
        redirect: to => ({
            path: `/${getUserPreferredLanguage()}/dashboard`,
            query: to.query,
            hash: to.hash
        })
    },
    {
        path: '/:lang',
        beforeEnter: (to, from, next) => {
            const lang = to.params.lang;

            if (!supportedLanguages.includes(lang)) {
                return next(`/${defaultLanguage}`);
            }

            return next();
        },
        children: [
            {
                path: '',
                redirect: to => {
                    const lang = to.params.lang;
                    if (!supportedLanguages.includes(lang)) {
                        // 非法语言（如 /foobar 被 /:lang 误消费）不要拼成 /foobar/home，
                        // 统一兜底到默认语言，由 /:lang 的 beforeEnter 继续处理。
                        return { path: `/${defaultLanguage}` };
                    }
                    return { path: `/${lang}/home` };
                }
            },
            {
                path: 'home',
                component: LandingLayout,
                children: [
                    {
                        path: '',
                        name: 'Landing',
                        component: () => import('../views/LandingPageView.vue'),
                        meta: {
                            requiresAuth: false
                        }
                    }
                ]
            },
            {
                path: 'landing',
                component: LandingLayout,
                children: [
                    {
                        path: '',
                        name: 'LandingPage',
                        component: () => import('../views/LandingPageView.vue'),
                        meta: {
                            requiresAuth: false
                        }
                    }
                ]
            },
            {
                path: 'login',
                name: 'login',
                component: LoginView,
                meta: {
                    requiresAuth: false
                }
            },
            {
                path: '',
                component: AppLayout,
                children: [
                    {
                        path: '',
                        name: 'upload',
                        component: () => import('../views/HomeView.vue'),
                        meta: {
                            requiresAuth: false
                        }
                    },
                    {
                        path: 'upload',
                        name: 'upload',
                        component: () => import('../views/HomeView.vue'),
                        meta: {
                            requiresAuth: false
                        }
                    },
                    {
                        path: 'result/:locator',
                        name: 'result',
                        component: createAsyncRouteComponentLoader('ResultView', () => import('../views/ResultView.vue')),
                        meta: {
                            requiresAuth: true
                        }
                    },
                    {
                        path: 'confirm',
                        name: 'confirm',
                        component: () => import('../views/ConfirmView.vue'),
                        meta: {
                            requiresAuth: false
                        }
                    },
                    {
                        path: 'auth-callback',
                        name: 'auth-callback',
                        component: () => import('../views/AuthCallbackView.vue'),
                        meta: { requiresAuth: false }
                    },
                    {
                        path: 'terms',
                        name: 'Terms',
                        component: () => import('../views/TermsView.vue'),
                        meta: {
                            requiresAuth: false
                        }
                    },
                    {
                        path: 'privacy',
                        name: 'Privacy',
                        component: () => import('../views/PrivacyView.vue'),
                        meta: {
                            requiresAuth: false
                        }
                    },
                    {
                        path: 'history',
                        name: 'history',
                        component: () => import('../views/HistoryView.vue'),
                        meta: {
                            requiresAuth: true
                        }
                    },
                    {
                        path: 'about',
                        name: 'about',
                        component: () => import('../views/AboutView.vue'),
                        meta: {
                            requiresAuth: false
                        }
                    },
                    {
                        path: 'payment/cancel',
                        name: 'payment-cancel',
                        component: () => import('../views/PaymentCancelView.vue'),
                        meta: {
                            requiresAuth: true
                        }
                    },
                    {
                        path: 'dashboard',
                        name: 'dashboard',
                        component: () => import('../views/DashboardView.vue'),
                        meta: {
                            requiresAuth: true
                        }
                    },
                    {
                        path: 'tools',
                        name: 'tools',
                        component: () => import('../views/FreeToolsView.vue'),
                        meta: {
                            requiresAuth: false
                        }
                    },
                    {
                        path: 'blog',
                        name: 'blog-list',
                        component: () => import('../views/BlogListView.vue'),
                        meta: {
                            requiresAuth: false
                        }
                    },
                    {
                        path: 'blog/category/:categorySlug',
                        name: 'blog-category',
                        component: () => import('../views/BlogTaxonomyView.vue'),
                        meta: {
                            requiresAuth: false,
                            taxonomyType: 'category'
                        }
                    },
                    {
                        path: 'blog/tag/:tagSlug',
                        name: 'blog-tag',
                        component: () => import('../views/BlogTaxonomyView.vue'),
                        meta: {
                            requiresAuth: false,
                            taxonomyType: 'tag'
                        }
                    },
                    {
                        path: 'blog/search',
                        name: 'blog-search',
                        component: () => import('../views/BlogTaxonomyView.vue'),
                        meta: {
                            requiresAuth: false,
                            taxonomyType: 'search'
                        }
                    },
                    {
                        path: 'blog/:slug',
                        name: 'blog-detail',
                        component: () => import('../views/BlogDetailView.vue'),
                        meta: {
                            requiresAuth: false
                        }
                    },
                    {
                        path: 'case/:caseSlug',
                        name: 'public-result-case',
                        component: () => import(
                            /* webpackChunkName: "public-result-case" */
                            '../views/PublicResultCaseView.vue'
                        ),
                        meta: {
                            requiresAuth: false,
                            resultAccessMode: 'showcase'
                        }
                    }
                ]
            }
        ]
    }
];

// 收集 /:lang 下所有已知子路由的首段（含动态路由的首段，如 result/blog/case 等），
// 用于判断「无语言前缀的路径」是否为已知路由，从而决定是否补前缀重定向。
// 递归遍历子路由，自动覆盖新增路由，避免与 router.resolve 在守卫内的行为耦合。
function collectLocaleChildSegments(children, segments = new Set()) {
    for (const child of children) {
        if (child.children && child.children.length) {
            collectLocaleChildSegments(child.children, segments);
        }
        if (child.path && child.path !== '' && !child.path.startsWith(':')) {
            const first = child.path.split('/')[0];
            if (first && !first.startsWith(':')) {
                segments.add(first);
            }
        }
    }
    return segments;
}

const langRoute = routes.find(r => r.path === '/:lang');
const knownLocaleSegments = langRoute
    ? collectLocaleChildSegments(langRoute.children)
    : new Set();

// 注入「无 locale 前缀的已知页面」重定向路由：放在 /:lang 之前，避免 /terms 被 /:lang
// 误当成 lang=terms 后再被空路径重定向拼成 /terms/home。正则首段限定为已知页面，
// 自动排除 en/zh/ja，因此 /en/terms 这类合法路径不会被此路由匹配，自然落到 /:lang。
const localeLessRedirectRoute = {
    path: `/:first(${[...knownLocaleSegments].join('|')})/:rest(.*)?`,
    redirect: to => {
        const locale = getUserPreferredLanguage();
        const rest = to.params.rest ? `/${to.params.rest}` : '';
        return { path: `/${locale}/${to.params.first}${rest}`, query: to.query, hash: to.hash };
    }
};
const langRouteIndex = routes.findIndex(r => r.path === '/:lang');
if (langRouteIndex >= 0) {
    routes.splice(langRouteIndex, 0, localeLessRedirectRoute);
}

const permissionGuardEnabled = featurePermissionAlpha;
const analysisRuntimePreloadRouteNames = new Set([
    'upload',
    'result'
]);

function shouldPreloadAnalysisRuntime(to) {
    return analysisRuntimePreloadRouteNames.has(to.name);
}

function scheduleAnalysisRuntimePreload(to) {
    const runPreload = () => {
        const workerStore = useWorkerStore();
        workerStore.preloadWorker().catch(error => {
            console.warn('Worker preload failed for route:', to.name, error.message);
        });

    };

    if (runtimeEnv.isProduction && typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(runPreload, { timeout: 5000 });
        return;
    }

    window.setTimeout(runPreload, runtimeEnv.isProduction ? 1500 : 0);
}

const router = createRouter({
    history: createWebHistory(),
    routes,
    scrollBehavior(to, from, savedPosition) {
        // 如果有保存的位置（例如用户使用浏览器的后退按钮），则使用保存的位置
        if (savedPosition) {
            return savedPosition;
        }
        // 带锚点（如 #pricing）时滚动到对应区块，预留固定头部高度
        if (to.hash) {
            return {
                el: to.hash,
                behavior: 'smooth',
                top: 80
            };
        }
        // 否则滚动到顶部
        return { top: 0 };
    },
});

router.beforeEach(async (to, from, next) => {
    // 对技术文件的特殊处理
    const technicalFiles = ['/sitemap.xml', '/robots.txt'];
    if (technicalFiles.includes(to.path)) {
        return next();
    }

    const userStore = useUserStore();
    const subscriptionStore = useSubscriptionStore();
    const requiresAuth = to.matched.some(record => record.meta.requiresAuth);

    // 检查是否来自搜索引擎爬虫
    const userAgent = navigator.userAgent.toLowerCase();
    const isBot = /bot|googlebot|crawler|spider|robot|crawling/i.test(userAgent);

    // 如果URL中没有语言前缀，重定向到适当的语言版本
    const pathLocale = getLocaleFromPath(to.path);
    if (!pathLocale) {
        // 爬虫访问根路径：使用默认语言
        if (isBot && to.path === '/') {
            return next(`/${defaultLanguage}`);
        }
        // 爬虫访问特定路径：保持当前路径（避免重复内容被抓取）
        if (isBot) {
            return next();
        }
        // 根路径：重定向到用户偏好语言（/en、/zh、/ja）
        if (to.path === '/') {
            const targetLocale = getUserPreferredLanguage();
            return next(`/${targetLocale}`);
        }
        // 其余无前缀路径交由路由层 locale-less 重定向路由处理（见 routes 中 :first(...) 重定向）：
        // 已知页面补上前缀（/terms → /{locale}/terms），未知路径（如 /favicon.ico）保持原样，
        // 由 /:lang 守卫兜底，避免无效路径变成空白页。
        return next();
    }

    // 更新语言设置
    const lang = to.params.lang;
    i18n.global.locale.value = lang;
    document.documentElement.lang = lang;
    // 持久化最近一次有效 locale：Stripe 外跳会重新加载页面，内存 locale 会重置，
    // 回跳时 getUserPreferredLanguage() 需要读取 localStorage 才能恢复支付发起时的语言。
    // 此处仅在经过 getLocaleFromPath 校验的合法 locale 路由上写入（pathLocale 已非 null）。
    localStorage.setItem('language', lang);

    // 首页按登录态分流：已登录用户进入 upload，未登录展示 Landing。
    // 必须在分流前 hydrate 一次 checkAuth()：Landing 是 requiresAuth:false，
    // 全局守卫默认不会调用 checkAuth()，而 isLoggedIn 初始为 false，直接读会误判已登录用户。
    if (to.name === 'Landing') {
        if (!userStore.isLoggedIn) {
            try {
                await userStore.checkAuth();
            } catch (error) {
                // 认证检查失败视为未登录，停留 Landing，不阻断导航
                console.error('[Router Guard] Home auth check failed:', error);
            }
        }
        if (userStore.isLoggedIn) {
            return next({ name: 'upload', params: { lang: to.params.lang } });
        }
    }

    if (requiresAuth) {
        try {
            // 如果用户已经在内存中标记为已登录，避免重复的网络请求
            if (!userStore.isLoggedIn) {
                console.log('[Router Guard] 用户未登录，执行认证检查');
                await userStore.checkAuth();
            } else {
                console.log('[Router Guard] 用户已在内存中标记为已登录，跳过认证检查');
            }

            if (!userStore.isLoggedIn) {
                const redirect = to.fullPath;
                next({
                    path: `/${to.params.lang}/login`,
                    query: { redirect } // 保存目标路径
                });
                return;
            }
        } catch (error) {
            console.error('[Router Guard] Auth check failed:', error);
            // 保留原目标以便重试登录（方案 §9.5），且不把异常原文放入 query
            next({
                path: `/${to.params.lang}/login`,
                query: { redirect: to.fullPath }
            });
            return;
        }
    }

    if (permissionGuardEnabled) {
        const metaRecords = to.matched.map(record => record.meta || {});

        const requiredTiers = metaRecords
            .map(meta => meta.requiresTier)
            .filter(Boolean);

        if (requiredTiers.length > 0) {
            let strictestTier = requiredTiers[0];
            const priorityMap = subscriptionStore.tierPriority || {};
            requiredTiers.forEach(tierId => {
                if (!(tierId in priorityMap)) {
                    return;
                }
                if (!(strictestTier in priorityMap) || priorityMap[tierId] > priorityMap[strictestTier]) {
                    strictestTier = tierId;
                }
            });

            if (!subscriptionStore.isTierAtLeast(strictestTier)) {
                showPermissionBlocked('tier-insufficient', {
                    routeName: to.name,
                    requiredTierId: strictestTier,
                    currentTierId: subscriptionStore.currentTierId
                });
                return next(false);
            }
        }
    }

    // Preload analysis runtime only on analysis entry routes.
    // Landing/Login and other content/auth pages must not start OpenCV or Worker in any environment.
    if (shouldPreloadAnalysisRuntime(to)) {
        scheduleAnalysisRuntimePreload(to);
    }

    next();
});

router.beforeResolve((to, from, next) => {
    if (to.name === 'result') {
        console.log('[PSDBG][router] beforeResolve result', {
            from: from.fullPath,
            to: to.fullPath
        });
    }
    next();
});

router.afterEach((to, from, failure) => {
    if (to.name === 'result' || from.name === 'result') {
        console.log('[PSDBG][router] afterEach result', {
            from: from.fullPath,
            to: to.fullPath,
            failureType: failure?.type || null,
            failureMessage: failure?.message || null
        });
    }
});

// 记录本次导航的触发类型（push/replace/back/forward/initial/unknown），
// 供 Landing/Upload 等页面 view 事件回填 navigation_trigger / source_position。
// 必须在页面组件 onMounted 之前执行：Vue Router 在 finalizeNavigation 后同步调用 afterEach，
// 而组件挂载发生在 currentRoute 变更后的下一个微任务，故此处写入的值对 onMounted 可见。
router.afterEach(() => {
  recordNavigationTrigger();
});

// v2.1.13：所有页面进入后评估计费通知（首次发放由 store watcher 捕获；未消费提醒由 maybeShowReminder 评估）。
// 用 afterEach 而非 beforeEach：首屏首次导航早于 app mount，Pinia/store 可能未就绪；
// afterEach 在导航完成后执行，store 已可用，且不阻塞用户看到目标页。
// 未登录用户在 evaluateOnRouteEntry 内由 isLoggedIn 提前返回，故公开页/落地页不会误弹。
router.afterEach(() => {
    // 非阻塞触发；内部 syncStatus 失败会被吞掉（沿用 billing 失败隔离）。
    evaluateOnRouteEntry().catch((e) => {
        console.warn('[billing-notice] route-entry eval failed:', e);
    });
});

export default router;
